/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — classic trunk-and-bus tree routing (People Rebuild,
   Josh grant 2026-08-17: "the auto-routes are trash — I expect
   CLASSIC ORG-CHART lines").

   Pure logic, NO React/xyflow imports — a small, separate router that
   runs BEFORE the plain-straight-segment fallback every other auto
   edge gets (see edgeGeometry.ts's `buildEdgePoints`/`snapPlainPair`).
   It only ever claims a narrow, well-defined shape: an AUTO (empty
   waypoints) member→member edge whose child sits strictly BELOW its
   parent. Every edge sharing one SOURCE (one parent) is a "family"
   and shares ONE geometry:

     - busY = midpoint between the parent's bottom and the family's
       HIGHEST child top (min child top), clamped >=16px off both
       where the gap allows it.
     - Every family edge is the SAME 4-point polyline shape: parent
       bottom-center -> (parentCenterX, busY) -> (childCenterX, busY)
       -> child top-center. Because every edge shares the identical
       first TWO points, the overlapping strokes render as one visual
       trunk + one horizontal bus with clean drops — the textbook
       look. A child whose center lines up with the parent's collapses
       to a plain 2-point straight drop (no dogleg to draw).
     - If the bus segment would cross a non-family node, busY nudges
       in 8px steps — toward the children first (busY grows, since
       children sit below), then away (busY shrinks), up to ±64px —
       to the first clear lane. No clear lane found = keep the
       midpoint; overlap beats spaghetti (Josh law, this brief).

   Edges the pattern doesn't claim (child above/level, junction/team
   endpoints, anything not a strict-below member pair) are simply
   absent from the returned map — the caller renders every one of
   those as a plain straight anchor-to-anchor segment instead (People
   Rebuild, Josh law 2026-08-18: the obstacle-avoiding A* router this
   comment used to hand off to is RETIRED — see edgeGeometry.ts).

   ALIGNMENT COLLAPSE (2026-08-18 update): widened from a near-zero
   (<0.5px) equality check to ALIGN_COLLAPSE_PX (8px) — "zero turns =
   one straight line = default" applies inside a family exactly like
   it does everywhere else. A child within that tolerance of its
   parent's centerline gets a plain 2-point straight drop, x snapped
   to the CHILD's center (the more visually load-bearing of the two —
   the drop should look like it's falling straight out of the child,
   not the parent); its off-axis siblings are untouched and still get
   the full bus.
   ═══════════════════════════════════════════════════════════════ */

import type { ObstacleRect, Pt } from './edgeGeometry';

export interface TreeEdgeInput {
  id: string;
  source: string;
  target: string;
}

export interface TreeObstacle {
  nodeId: string;
  rect: ObstacleRect;
}

const MIN_BUS_CLEARANCE = 16; // px — kept off the parent's bottom and the nearest child's top where the gap allows it
const OBSTACLE_STEP = 8; // px — bus-nudge increment
const MAX_OBSTACLE_SHIFT = 64; // px — nudge search cap per direction
const OBSTACLE_PAD = 12; // px — matches the org-wide obstacle-clearance convention
const ALIGN_COLLAPSE_PX = 8; // px — Josh law 2026-08-18: within this, a drop is dead straight, no bus jog

function centerX(r: ObstacleRect): number {
  return r.x + r.width / 2;
}
function bottomY(r: ObstacleRect): number {
  return r.y + r.height;
}
function topY(r: ObstacleRect): number {
  return r.y;
}

/** True if a horizontal run at `y` from `minX` to `maxX` passes through
 *  the padded interior of any rect in `rects`. Touching a padded edge
 *  exactly (e.g. running along y = a rect's boundary) is allowed —
 *  the router hugs the padding rather than treating it as blocked. */
function busSegmentBlocked(y: number, minX: number, maxX: number, rects: ObstacleRect[]): boolean {
  for (const r of rects) {
    const rx0 = r.x - OBSTACLE_PAD;
    const rx1 = r.x + r.width + OBSTACLE_PAD;
    const ry0 = r.y - OBSTACLE_PAD;
    const ry1 = r.y + r.height + OBSTACLE_PAD;
    if (y > ry0 && y < ry1 && maxX > rx0 && minX < rx1) return true;
  }
  return false;
}

/** Compute the trunk-and-bus polyline (INCLUDING both endpoints) for
 *  every tree-routable edge. `nodeBounds` is scoped to MEMBER nodes
 *  only by the caller — an edge whose source or target isn't in it
 *  (junction/team endpoint) is never considered, which is how
 *  "member→member only" is enforced.
 *  `obstacles` is the FULL node obstacle set (every node type) used
 *  for the bus-nudge check. */
export function computeTreeRoutes(
  edges: TreeEdgeInput[],
  nodeBounds: Map<string, ObstacleRect>,
  obstacles: TreeObstacle[]
): Map<string, Pt[]> {
  const result = new Map<string, Pt[]>();

  const families = new Map<string, TreeEdgeInput[]>();
  for (const e of edges) {
    const p = nodeBounds.get(e.source);
    const c = nodeBounds.get(e.target);
    if (!p || !c) continue;
    if (!(topY(c) > bottomY(p))) continue; // not strictly below — the plain straight-segment fallback owns it
    const arr = families.get(e.source);
    if (arr) arr.push(e);
    else families.set(e.source, [e]);
  }

  for (const [sourceId, familyEdges] of families) {
    const parentRect = nodeBounds.get(sourceId)!;
    const pBottom = bottomY(parentRect);
    const pCenterX = centerX(parentRect);
    const children = familyEdges.map((e) => ({ edge: e, rect: nodeBounds.get(e.target)! }));
    const minChildTop = Math.min(...children.map((c) => topY(c.rect)));

    let busY = (pBottom + minChildTop) / 2;
    const lo = pBottom + MIN_BUS_CLEARANCE;
    const hi = minChildTop - MIN_BUS_CLEARANCE;
    if (lo <= hi) busY = Math.min(Math.max(busY, lo), hi);

    const childCenterXs = children.map((c) => centerX(c.rect));
    const busMinX = Math.min(pCenterX, ...childCenterXs);
    const busMaxX = Math.max(pCenterX, ...childCenterXs);

    const childIds = new Set(familyEdges.map((e) => e.target));
    const externalObstacles = obstacles
      .filter((o) => o.nodeId !== sourceId && !childIds.has(o.nodeId))
      .map((o) => o.rect);

    if (busSegmentBlocked(busY, busMinX, busMaxX, externalObstacles)) {
      const original = busY;
      let found = false;
      // Toward the children first (bus grows downward, since children
      // sit below the parent)...
      for (let step = OBSTACLE_STEP; step <= MAX_OBSTACLE_SHIFT && !found; step += OBSTACLE_STEP) {
        const candidate = original + step;
        if (!busSegmentBlocked(candidate, busMinX, busMaxX, externalObstacles)) {
          busY = candidate;
          found = true;
        }
      }
      // ...then away (bus shrinks back toward the parent).
      for (let step = OBSTACLE_STEP; step <= MAX_OBSTACLE_SHIFT && !found; step += OBSTACLE_STEP) {
        const candidate = original - step;
        if (!busSegmentBlocked(candidate, busMinX, busMaxX, externalObstacles)) {
          busY = candidate;
          found = true;
        }
      }
      // Neither direction cleared within the cap — keep the midpoint;
      // overlap beats spaghetti (brief, explicit).
    }

    for (const { edge, rect: childRect } of children) {
      const childCenterX = centerX(childRect);
      const childTop = topY(childRect);
      const targetPt: Pt = { x: childCenterX, y: childTop };
      if (Math.abs(childCenterX - pCenterX) <= ALIGN_COLLAPSE_PX) {
        // Aligned within tolerance — dead-straight drop, x snapped to
        // the child's own center (Josh law 2026-08-18).
        result.set(edge.id, [{ x: childCenterX, y: pBottom }, targetPt]);
      } else {
        const sourcePt: Pt = { x: pCenterX, y: pBottom };
        result.set(edge.id, [
          sourcePt,
          { x: pCenterX, y: busY },
          { x: childCenterX, y: busY },
          targetPt,
        ]);
      }
    }
  }

  return result;
}
